import { camelCase } from 'lodash';
import { DESSERT, DESSERT_MULTI, DRINK, KIDS_MAIN, KIDS_SNACK, LUNCH, MAIN, SIDE, SNACK, STAPLE } from '../Constants';
import { isMealPlanMain, MealPlanFoodItemFields } from '../MealPlanUtils';
import { getMealPlanMerchandisingRegionId } from './MerchandisingRegion';

type ConsumerPrice = {
  default: number;
  [key: number]: number;
};

export const WSS_BASE_PRICE_FROM_TYPE_BY_REGION: { [key: string]: ConsumerPrice } = {
  [MAIN]: {
    default: 1350,
    1: 1450,
    2: 1500,
    7: 1400,
    8: 1450,
    9: 1400,
    10: 1400,
  },
  [SIDE]: {
    default: 600,
  },
  [STAPLE]: {
    default: 300,
  },
  [LUNCH]: {
    default: 1400,
    1: 1500,
    2: 1500,
    7: 1400,
    8: 1500,
    9: 1400,
    10: 1400,
  },
  [KIDS_MAIN]: {
    default: 900,
  },
  [KIDS_SNACK]: {
    default: 600,
  },
  [DESSERT]: {
    default: 300,
  },
  [DESSERT_MULTI]: {
    default: 1200,
  },
  // These are not finalized yet, but are included for completeness
  [DRINK]: {
    default: 600,
  },
  [SNACK]: {
    default: 600,
  },
};

const lookupBasePrice = (foodType: string, regionId: number): number => {
  const foodTypePricing = WSS_BASE_PRICE_FROM_TYPE_BY_REGION[camelCase(foodType)];
  if (!foodTypePricing) {
    return 0;
  }

  return foodTypePricing[regionId] ?? foodTypePricing.default;
};

export const getWSSPriceFromTypeByRegion = (foodType: string, regionId: number): number =>
  lookupBasePrice(foodType, getMealPlanMerchandisingRegionId(regionId));

/**
 * Cents one serving of a dish costs on top of the plan price: the base price for its type and
 * region plus its tier. A main's base is the plan's own per-serving price, so a main returns its
 * tier alone.
 */
export const getWSSPricePerServing = (
  foodItem: MealPlanFoodItemFields & { upsell?: { price: number } | null },
  regionId: number | undefined
): number => {
  const basePrice = isMealPlanMain(foodItem)
    ? 0
    : getWSSPriceFromTypeByRegion(foodItem.mealPlanFoodType ?? '', regionId ?? 0);
  return basePrice + (foodItem.upsell?.price ?? 0);
};

const NINETY_NINE_CENTS = 99;
const FORTY_NINE_CENTS = 49;

/**
 * Reads the shef's own region rather than resolving through
 * {@link getMealPlanMerchandisingRegionId}: this drives the price suggested to a shef editing a
 * dish, so it should reflect the market they cook in, not the market their food is driven to.
 */
export const getMarketplacePriceFromTypeByRegion = (foodType: string, regionId: number): number => {
  const wssBasePrice = lookupBasePrice(foodType, regionId);

  if (wssBasePrice === 0) {
    return wssBasePrice;
  }
  if ([MAIN, LUNCH].includes(camelCase(foodType))) {
    return wssBasePrice + NINETY_NINE_CENTS;
  }
  return wssBasePrice + FORTY_NINE_CENTS;
};
